import {
  addQueryParams,
  scrollToElement,
  SideMenuProps,
  useBreakpoint,
  useQueryParams,
  useRouter,
} from '@main/core-ui';
import { MenuTabInterface } from '@main/internationalization';
import { SkipLinkIds } from '@main/pc-types';
import AboutTranscendWidget from '@main/privacy-center/src/components/AboutTranscendWidget';
import { SidebarMobile } from '@main/privacy-center/src/components/SidebarMobile';
import { SideMenu } from '@main/privacy-center/src/components/SideMenu';
import {
  selectDisabledPrivacyCenterAboutTranscend,
  selectRequiredPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import { switchCase } from '@main/utils';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { sidebarMessages } from './messages';
import { useRouteSkipLink } from './useRouteSkipLink';
import { useSideMenuId } from './useSideMenuId';
import {
  DesktopSidebarWrapper,
  MobileSidebarWrapper,
  SidebarWrapper,
  WidgetWrapper,
} from './wrappers';

export interface SidebarProps {
  /** the tabs to display */
  tabs: MenuTabInterface[];
}

/**
 * Page Sidebar for desktop
 */
export const Sidebar: React.FC<React.PropsWithChildren<SidebarProps>> = ({
  tabs,
}) => {
  const {
    assets: { widgetBg },
  } = useSelector(selectRequiredPrivacyCenter);
  const disabledPrivacyCenterAboutTranscend = useSelector(
    selectDisabledPrivacyCenterAboutTranscend,
  );
  const { name } = useQueryParams();
  const sideMenuId = useSideMenuId();
  const { location, redirect } = useRouter();
  const { formatMessage } = useIntl();
  const { isBeforeTablet, isAtLeastTablet } = useBreakpoint();
  useRouteSkipLink();
  // The props to pass to side menu and mobile side menu
  const sideMenuProps: SideMenuProps = {
    tabs,
    selected: sideMenuId || name || tabs[0]?.visualId, // fallback to name for policies page (selects the slug)
    onSelectChange: ({ newMenu, menuTab, parentTab }) => {
      switchCase({
        PolicyMenuTab: () => {
          redirect({
            ...addQueryParams(location, { name: (parentTab as any)?.slug }),
            hash: newMenu,
          });
          // add delay here to allow page to rerender once from the redirect above
          setTimeout(() => {
            scrollToElement(
              newMenu || `policy-${(parentTab as any)?.slug}`,
              [0, 0],
            );
          }, 10);
        },
        Policy: () => {
          redirect({
            ...addQueryParams(location, { name: menuTab.slug }),
            hash: '',
          });
          // add delay here to allow page to rerender once from the redirect above
          setTimeout(() => {
            scrollToElement(`policy-${menuTab.slug}`, [0, 0]);
          }, 10);
        },
        DataCollection: () => {
          redirect({
            ...location,
            hash: newMenu,
          });
        },
        // eslint-disable-next-line no-underscore-dangle
      })(() => null)((menuTab as any)?.__typename);
    },
  };

  return (
    <SidebarWrapper
      id={SkipLinkIds.Sidebar}
      aria-label={formatMessage(sidebarMessages.navLabel)}
      tabIndex={-1}
    >
      {isBeforeTablet && tabs && tabs.length > 0 && (
        <MobileSidebarWrapper>
          <SidebarMobile {...sideMenuProps} />
        </MobileSidebarWrapper>
      )}
      {isAtLeastTablet && (
        <DesktopSidebarWrapper>
          <SideMenu {...sideMenuProps} />
          {!disabledPrivacyCenterAboutTranscend && (
            <WidgetWrapper>
              <AboutTranscendWidget asset={widgetBg} />
            </WidgetWrapper>
          )}
        </DesktopSidebarWrapper>
      )}
    </SidebarWrapper>
  );
};
